import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken } from "@/lib/http";
import { clearWidgetAuth, configureWidgetAuth } from "@/lib/widgetSync";
import { clearDashCache } from "@/lib/dashCache";

const AuthContext = createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// Capture the OAuth-callback state at MODULE LOAD time (i.e. when the JS bundle
// is first evaluated on page load). AuthCallback wipes the URL hash synchronously
// on mount, and because child effects run before the parent's useEffect in
// React, checking `window.location.hash` inside AuthProvider's useEffect is too
// late — the hash is already gone. This module-level constant is captured
// before ANY React effect runs, so we can reliably skip the /auth/me probe
// during the session_id exchange.
const IS_OAUTH_CALLBACK = (() => {
  try {
    return typeof window !== "undefined" && (window.location.hash || "").includes("session_id=");
  } catch {
    return false;
  }
})();

// Report the device locale region once per app session so the admin panel can
// show which country each user is from ("de-CH" → CH). Fallback: timezone map.
let _regionSent = false;
const _TZ_COUNTRY = {
  "Europe/Zurich": "CH", "Europe/Berlin": "DE", "Europe/Vienna": "AT",
  "Europe/Rome": "IT", "Europe/Paris": "FR", "Europe/Madrid": "ES",
  "Europe/Lisbon": "PT", "Europe/London": "GB", "Europe/Amsterdam": "NL",
  "Europe/Brussels": "BE", "Europe/Luxembourg": "LU", "Europe/Vaduz": "LI",
};
async function reportDeviceRegion() {
  if (_regionSent) return;
  _regionSent = true;
  try {
    const opts = Intl.DateTimeFormat().resolvedOptions();
    // Timezone FIRST: far more accurate than the locale region (many Swiss
    // iPhones are set to "Deutsch (Deutschland)" → would wrongly report DE).
    let country = opts.timeZone ? _TZ_COUNTRY[opts.timeZone] : null;
    if (!country) {
      const locale = opts.locale || navigator.language || "";
      country = (locale.match(/[-_]([A-Za-z]{2})(?=[-_]|$)/) || [])[1];
    }
    if (country) await api.post("/auth/device-region", { country: country.toUpperCase(), tz: opts.timeZone || null });
  } catch { /* non-critical */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from the Emergent Google OAuth callback, skip the
    // /me check. AuthCallback will POST /auth/session and call setUser(user)
    // once the exchange completes; firing /me here would race the exchange and
    // set user=null, bouncing the user back to /login.
    if (IS_OAUTH_CALLBACK) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Whenever a user session exists, hand the token to the iOS widget
  // (App Group) so its TimelineProvider can fetch stats autonomously.
  useEffect(() => {
    if (user) {
      configureWidgetAuth();
      reportDeviceRegion();
    }
  }, [user]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    try { await clearWidgetAuth(); } catch { /* ignore */ }
    clearDashCache(user?.user_id);
    setAuthToken(null);
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh: checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
